import { Box, Typography } from '@mui/material';
import { ScenarioStat } from 'types';

interface EfficiencyTableProps {
  data: ScenarioStat[];
}

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}М` : `${Math.round(n / 1000)}К`;

const statusLabel = (r: number): { label: string; bg: string; color: string } => {
  if (r >= 80) return { label: 'Активно', bg: 'rgba(5,150,105,0.10)', color: '#059669' };
  if (r >= 40) return { label: 'Средне', bg: 'rgba(217,119,6,0.10)', color: '#d97706' };
  return { label: 'Редко', bg: 'rgba(107,114,128,0.10)', color: '#6b7280' };
};

export default function EfficiencyTable({ data }: EfficiencyTableProps) {
  const max = Math.max(...data.map((d) => d.requests));

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid rgba(20,116,255,0.10)',
        boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
        borderRadius: 'var(--radius-lg)',
        p: 2.5,
        transition: 'box-shadow 0.20s ease',
        '&:hover': { boxShadow: '0 4px 24px rgba(20,116,255,0.12), 0 1px 4px rgba(0,0,0,0.04)' },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'var(--color-text-muted)',
          mb: 2,
        }}
      >
        Эффективность по сценариям
      </Typography>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <Box component="thead">
          <Box component="tr">
            {['Сценарий', 'Запросов', 'Токенов', 'Статус'].map((h) => (
              <Box
                key={h}
                component="th"
                sx={{
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  pb: 1,
                  borderBottom: '1px solid rgba(20,116,255,0.07)',
                }}
              >
                {h}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {data.map((d, i) => {
            const s = statusLabel((d.requests / max) * 100);
            return (
              <Box key={d.scenarioId} component="tr">
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    fontSize: 12.5,
                    color: 'var(--color-text-secondary)',
                    borderBottom: i < data.length - 1 ? '1px solid rgba(20,116,255,0.07)' : 'none',
                  }}
                >
                  {d.title}
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    borderBottom: i < data.length - 1 ? '1px solid rgba(20,116,255,0.07)' : 'none',
                  }}
                >
                  {d.requests}
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    fontSize: 12.5,
                    color: 'var(--color-text-muted)',
                    borderBottom: i < data.length - 1 ? '1px solid rgba(20,116,255,0.07)' : 'none',
                  }}
                >
                  {fmt(d.tokens)}
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    borderBottom: i < data.length - 1 ? '1px solid rgba(20,116,255,0.07)' : 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-block',
                      bgcolor: s.bg,
                      color: s.color,
                      borderRadius: '5px',
                      px: 0.875,
                      py: '2px',
                      fontSize: 10,
                      fontWeight: 700,
                      border: `1px solid ${s.color}22`,
                    }}
                  >
                    {s.label}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
